import React, { useState, useMemo } from "react";
import { ClientRecurringBillingItem } from "../types/clients-module-types";
import {
  dateFormatEntryEdit,
  currencyFormat,
} from "../../SharedModule/utils/formatters";
import {
  changeOrder,
  IsSeeRecurringActives,
  SelectOrderCriteria,
  SelectSearchTextRecurring,
  setSearchTextRecurring,
  setSeeRecurringActives,
} from "../redux/reducers/clientWork.reducer";
import { useDispatch, useSelector } from "react-redux";
import { NoContent } from "../../SharedModule/components/NoContent";
import image from "./../../styles/legacy/404.png";
import { actualUIState } from "../../SharedModule/redux/reducers/uiShared.reducer";
import { getBillPeriodText } from "../utils/clientsUtils";

export const AcordionRecurringBillingsTable = React.memo(
  ({
    clientRecurringBillings,
  }: {
    clientRecurringBillings: Array<ClientRecurringBillingItem> | null;
  }) => {
    const dispatch = useDispatch();

    const isSeeRecurringActives = useSelector(IsSeeRecurringActives);
    const selectSearchTextRecurring = useSelector(SelectSearchTextRecurring);

    const sortBy = useSelector(SelectOrderCriteria);

    const { asuitePermissions } = useSelector(actualUIState);
    const isASuiteRecurringBillingAdmin =
      asuitePermissions.includes("Billing_Admin");

    const [seeRecurringBillingsActive, setSeeRecurringBillingsActive] =
      useState(isSeeRecurringActives);
    const [recurringBillingsSearchText, setRecurringBillingsSearchText] =
      useState(selectSearchTextRecurring);

    const updateSeeRecurringBillingsActive = (value: boolean) => {
      dispatch(setSeeRecurringActives(value));
      setSeeRecurringBillingsActive(value);
    };

    let clientRecurringBillingsToShow: Array<ClientRecurringBillingItem> =
      useMemo(
        () =>
          clientRecurringBillings?.filter((elem) => {
            const lowerSearch = recurringBillingsSearchText.toLocaleLowerCase();
            return elem.description.toLocaleLowerCase().includes(lowerSearch);
          }) ?? [],
        [clientRecurringBillings, recurringBillingsSearchText]
      );

    const search = (value: string) => {
      setRecurringBillingsSearchText(value);
    };

    const blurHandler = () => {
      dispatch(setSearchTextRecurring(recurringBillingsSearchText));
    };

    const goToRecurringBillingDetails = (id: number) => {
      const baseUrl =
        process.env.REACT_APP_ASUITE_ADMIN_BILLING_RECURRING_EDIT ?? "";
      const newWindow = window.open(baseUrl + id + "&act=edit", "_blank");
      if (newWindow) {
        newWindow.focus();
      }
    };

    return (
      <>
        {!clientRecurringBillings ||
          (clientRecurringBillings.length === 0 && (
            <NoContent
              title="No Recurring Services for this Client"
              image={image}
              style={{ margin: "0 auto" }}
            />
          ))}

        {clientRecurringBillings && clientRecurringBillings.length > 0 && (
          <>
            <div className="d-flex mb-3 mx-2 justify-content-between">
              <div className="d-flex flex-column col-sm-9">
                <label htmlFor="recurring-search" className="col-form-label">
                  Search
                </label>
                <input
                  id="recurring-search"
                  type="text"
                  onBlur={blurHandler}
                  className="form-control form-control-sm common-input"
                  placeholder="Search by description"
                  value={recurringBillingsSearchText}
                  onChange={(event) => search(event.target.value)}
                />
              </div>

              <fieldset className="d-flex flex-column">
                <div className="col-form-label">Service Status</div>
                <div className="btn-group w-100">
                  <button
                    type="button"
                    onClick={() => updateSeeRecurringBillingsActive(true)}
                    className={`btn btn-group-custom ${
                      !seeRecurringBillingsActive ? "btn-light" : "btn-active"
                    }`}
                    disabled={seeRecurringBillingsActive}
                  >
                    Active
                  </button>
                  <button
                    type="button"
                    onClick={() => updateSeeRecurringBillingsActive(false)}
                    className={`btn btn-group-custom ${
                      !seeRecurringBillingsActive ? "btn-inactive" : "btn-light"
                    }`}
                    disabled={!seeRecurringBillingsActive}
                  >
                    Inactive
                  </button>
                </div>
              </fieldset>
            </div>

            {clientRecurringBillingsToShow?.filter((elem) =>
              seeRecurringBillingsActive ? elem.active : !elem.active
            ).length === 0 && (
              <NoContent
                title="No Results"
                text="Edit your filters to find the work items you are looking for."
                image={image}
                style={{ margin: "0 auto" }}
              />
            )}

            {clientRecurringBillingsToShow?.filter((elem) =>
              seeRecurringBillingsActive ? elem.active : !elem.active
            ).length > 0 && (
              <table
                className="table grey-table-header-background mt-4 mb-2"
                style={{ width: "100%" }}
              >
                <thead className="align-middle sticky sticky-header">
                  <tr>
                    <th
                      className={`border-dark border-top fw-600 ps-4 column-20 pointer ${
                        sortBy.orderBy === "description"
                          ? "sorted-" + sortBy.criteria
                          : ""
                      }`}
                      id="description"
                      key="description"
                      onClick={() =>
                        dispatch(
                          changeOrder({
                            orderBy: "description",
                            dataType: "string",
                          })
                        )
                      }
                    >
                      Description
                    </th>
                    <th
                      className={`border-dark border-top fw-600 ps-4 column-8 pointer ${
                        sortBy.orderBy === "startDate"
                          ? "sorted-" + sortBy.criteria
                          : ""
                      }`}
                      id="startDate"
                      key="startDate"
                      onClick={() =>
                        dispatch(
                          changeOrder({
                            orderBy: "startDate",
                            dataType: "string",
                          })
                        )
                      }
                    >
                      Start
                    </th>
                    <th
                      className={`border-dark border-top fw-600 ps-4 column-8 pointer ${
                        sortBy.orderBy === "endDate"
                          ? "sorted-" + sortBy.criteria
                          : ""
                      }`}
                      id="endDate"
                      key="endDate"
                      onClick={() =>
                        dispatch(
                          changeOrder({
                            orderBy: "endDate",
                            dataType: "string",
                          })
                        )
                      }
                    >
                      End
                    </th>
                    <th
                      className={`border-dark border-top fw-600 text-center column-5 pointer ${
                        sortBy.orderBy === "quantity"
                          ? "sorted-" + sortBy.criteria
                          : ""
                      }`}
                      id="quantity"
                      key="quantity"
                      onClick={() =>
                        dispatch(
                          changeOrder({
                            orderBy: "quantity",
                            dataType: "number",
                          })
                        )
                      }
                    >
                      Qty
                    </th>
                    <th
                      className={`border-dark border-top fw-600 ps-1 text-center column-12point5 pointer ${
                        sortBy.orderBy === "pricePerMonth"
                          ? "sorted-" + sortBy.criteria
                          : ""
                      }`}
                      id="pricePerMonth"
                      key="pricePerMonth"
                      onClick={() =>
                        dispatch(
                          changeOrder({
                            orderBy: "pricePerMonth",
                            dataType: "number",
                          })
                        )
                      }
                    >
                      Price per Month
                    </th>
                    <th
                      className={`border-dark border-top fw-600 text-center column-12point5 pointer ${
                        sortBy.orderBy === "billPeriod"
                          ? "sorted-" + sortBy.criteria
                          : ""
                      }`}
                      id="billPeriod"
                      key="billPeriod"
                      onClick={() =>
                        dispatch(
                          changeOrder({
                            orderBy: "billPeriod",
                            dataType: "number",
                          })
                        )
                      }
                    >
                      Billing Period
                    </th>
                    <th
                      className={`border-dark border-top fw-600 text-center column-10 pointer ${
                        sortBy.orderBy === "billThruDate"
                          ? "sorted-" + sortBy.criteria
                          : ""
                      }`}
                      id="billThruDate"
                      key="billThruDate"
                      onClick={() =>
                        dispatch(
                          changeOrder({
                            orderBy: "billThruDate",
                            dataType: "string",
                          })
                        )
                      }
                    >
                      Billed Thru
                    </th>
                    <th
                      className={`border-dark border-top fw-600 ps-4 column-24 pointer ${
                        sortBy.orderBy === "memo"
                          ? "sorted-" + sortBy.criteria
                          : ""
                      }`}
                      id="memo"
                      key="memo"
                      onClick={() =>
                        dispatch(
                          changeOrder({
                            orderBy: "memo",
                            dataType: "string",
                          })
                        )
                      }
                    >
                      Memo
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {clientRecurringBillingsToShow
                    ?.filter((elem) =>
                      seeRecurringBillingsActive ? elem.active : !elem.active
                    )
                    .map((item: ClientRecurringBillingItem) => {
                      return (
                        <tr
                          key={item.id}
                          onClick={() =>
                            isASuiteRecurringBillingAdmin
                              ? goToRecurringBillingDetails(item.id)
                              : null
                          }
                          className={`${
                            isASuiteRecurringBillingAdmin
                              ? "client-contant-row"
                              : "client-contant-row-no-clickable"
                          }`}
                        >
                          <td className="ps-4 fw-500">{item.description}</td>
                          <td className="ps-4 fw-500">
                            {dateFormatEntryEdit(item.startDate)}
                          </td>
                          <td className="ps-4 fw-500">
                            {item.endDate
                              ? dateFormatEntryEdit(item.endDate)
                              : ""}
                          </td>
                          <td className="text-center fw-500">
                            {item.quantity}
                          </td>
                          <td
                            className="text-end fw-500"
                            style={{ paddingRight: "2.5rem" }}
                          >
                            {currencyFormat(item.pricePerMonth)}
                          </td>
                          <td className="text-center fw-500">
                            {getBillPeriodText(item.billPeriod)}
                          </td>
                          <td className="text-center fw-500">
                            {item.billThruDate
                              ? dateFormatEntryEdit(item.billThruDate)
                              : ""}
                          </td>
                          <td className="ps-4 fw-500">{item.memo}</td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            )}
          </>
        )}
      </>
    );
  }
);
